<template>
  <NewBsBox
    :bs="{ display: 'inline-flex', alignItems: 'center' }"
  >
    <slot
      :state="{
        currentIndex: index,
        setActive: setActive,
      }"
    >
      <template
        v-for="(item, i) of translist"
        :key="item.name"
      >
        <BeerButton
          :bs="item.bs"
          :size="size"
          :disabled="item.disabled"
          :data-selected="item.active"
          :variant="item.active ? activeVariant : variant"
          :modifier="item.active ? activeModifier : modifier"
          v-bind="item.binds || {}"
          @click="setActive(i, item)"
        >
          {{ item.name }}
        </BeerButton>
        <Divider
          v-if="divider && i < translist.length - 1"
          :bs="dividerBs"
          vertical
          hair-line
        />
      </template>
    </slot>
  </NewBsBox>
</template>

<script lang="ts">
  import {
    PropType, defineComponent, computed, ref, watch,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import { Divider } from '@xhs/yam-beer/cmp/common/Divider'
  import BeerButton, { TypeSize, TypeVariant } from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import { BeerStyleObj, ModifierColor } from '../../../core/index'

  interface IlistItem {
    name: string
    disabled: boolean
    bs: BeerStyleObj
    binds: Record<string, unknown>
  }

  const getExtraBs = (
    active: boolean,
    index: number,
    listNum: number,
    size: TypeSize,
    divider: boolean,
    compact: boolean,
  ) => {
    if (size === 'nm' && divider) {
      const extra: Record<string, unknown> = {}
      if (index === 0) {
        extra.mr = 12
      } else if (index === listNum - 1) {
        extra.ml = 12
        extra.mr = 0
      } else {
        extra.mx = 12
      }
      return {
        ...extra,
      }
    }
    if (compact) {
      return {
        mr: -1,
        borderRadius: 0,
        border: active ? 'brandoutline' : 'outlineoutline',
        zIndex: active ? 5 : 1,
        position: 'relative',
        '&:first-of-type': {
          borderTopLeftRadius: 'control',
          borderBottomLeftRadius: 'control',
        },
        '&:last-of-type': {
          mr: 0,
          borderTopRightRadius: 'control',
          borderBottomRightRadius: 'control',
        },
      }
    }
    return {}
  }

  export default defineComponent({
    name: 'ButtonGroup',
    components: {
      Divider,
      NewBsBox,
      BeerButton,
    },

    props: {
      /**
       * @deprecated 4.1.0
       *
       * 请使用 options
       */
      list: {
        type: Array as PropType<IlistItem[]>,
        required: false,
        default: () => ([]),
      },
      /**
       * button item 数组，要求带 name 属性， 可传 binds 属性覆盖按钮 props，disabled用于控制按钮的是否禁用
       */
      options: {
        type: Array as PropType<IlistItem[]>,
        required: false,
        default: () => ([]),
      },
      /**
       * 选中 button name
       */
      modelValue: {
        type: String,
      },
      /**
       * 按钮 size，和 BeerButton size 一致
       * @type {s|m|l}
       */
      size: {
        type: String as PropType<TypeSize>,
        default: 'm',
      },
      /**
       * 选中按钮样式风格的类型
       * @type {default|secondary|outline|text}
       */
      activeVariant: {
        type: String as PropType<TypeVariant>,
        default: 'default',
      },
      /**
       * 按钮样式风格类型
       * @type {default|secondary|outline|text}
       */
      variant: {
        type: String as PropType<TypeVariant>,
        default: 'stroke',
      },
      /**
       * 选中按钮的 modifier 状态
       * @type {body|primary|secondary|danger|success}
       */
      activeModifier: {
        type: String as PropType<ModifierColor>,
        default: 'brand',
      },
      /**
       * 按钮 modifier 状态
       * @type {body|primary|secondary|danger|success}
       */
      modifier: {
        type: String as PropType<ModifierColor>,
        default: 'body',
      },
      /**
       * 是否紧凑风格
       */
      compact: {
        type: Boolean as PropType<boolean>,
        default: true,
      },
      /**
       * 是否竖线分割, 在 size = nm 时生效
       */
      divider: {
        type: Boolean as PropType<boolean>,
        default: false,
      },
      /**
       * 分割线的 beerStyle
       * @ignore
       */
      dividerBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({ height: 16 }),
      },
      /**
       * 在 ModelValue 是空的时候，是否默认第一个
       */
      defaultFirst: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const list = computed(() => {
        if (props.options?.length > 0) {
          return props.options
        }
        return props.list
      })
      function getValue(myProps: typeof props) {
        if (props.defaultFirst) {
          return myProps.modelValue || (list.value[0]?.name)
        }
        return myProps.modelValue
      }

      const localName = ref(getValue(props))
      watch(() => props.modelValue, () => {
        localName.value = getValue(props)
      })

      const translist = computed(() => list.value.map((i, curIndex) => {
        const { bs = {} } = i
        const isActive = localName.value === i.name

        return {
          ...i,
          active: isActive || null,
          bs: {
            ...getExtraBs(
              isActive,
              curIndex,
              props.list.length,
              props.size,
              props.divider,
              props.compact,
            ),
            ...bs,
          },
        }
      }))

      const setActive = (i: number, item: IlistItem) => {
        localName.value = item.name

        emit('update:modelValue', item.name, {
          index: i,
          ...item,
        })
      }

      return {
        // local 值是在用户没有传 active props 时使用
        index: localName,
        translist,
        setActive,
      }
    },
  })
</script>
